import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PriorityHigh as PriorityHighIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Assessment as ReportsIcon,
  Help as HelpIcon,
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  Tag as TagIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
  Label as LabelIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
  NewReleases as ReleaseIcon,
  MeetingRoom as RoomManageIcon,
} from '@mui/icons-material';
import { useNavigationStats } from '../../../hooks/useNavigationStats';
import socketClient from '../../../utils/socket';
import { useOperatorStats } from '../../../hooks/useOperatorStats';
import { useTagStats } from '../../../hooks/useTagStats';
import { useFixedTagStats } from '../../../hooks/useFixedTagStats';
import CreateChatRoomModal from '../CreateChatRoomModal';
import { usePermissions } from '../../RoleBasedRoute';
import { devLog, devError } from '../../../utils/logger';

// データ型定義
interface InboxItem {
  id: string;
  name: string;
  count: number;
  status: 'pending' | 'active' | 'waiting' | 'completed' | 'temp_completed' | 'paused' | 'archived';
  priority: 'high' | 'medium' | 'low';
}

interface OperatorItem {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'operator';
  status: 'online' | 'offline' | 'away';
  activeChats: number;
  totalChats: number;
}

interface TagItem {
  id: string;
  name: string;
  color: string;
  count: number;
  category: string;
}

interface NavigationSidebarProps {
  selectedCategory: string;
  onCategorySelect: (categoryId: string) => void;
  compactMode?: boolean;
  onToggleCompact?: () => void;
  onChatRoomCreate?: () => void;
  onOperatorManage?: () => void;
  onTagManage?: () => void;
  onSystemSettings?: () => void;
  onAnalytics?: () => void;
  onAccounts?: () => void;
  onRefreshProvided?: (refreshFn: () => void) => void; // 統計更新関数を親に渡す
}

export const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  selectedCategory,
  onCategorySelect,
  compactMode = false,
  onToggleCompact,
  onChatRoomCreate,
  onOperatorManage,
  onTagManage,
  onSystemSettings,
  onAnalytics,
  onAccounts,
  onRefreshProvided,
}) => {
  const { isAdmin } = usePermissions();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    try {
      const adminUserStr = localStorage.getItem('adminUser');
      if (adminUserStr) {
        const userId = JSON.parse(adminUserStr)?.id;
        if (userId) {
          const saved = localStorage.getItem(`nav_accordion_${userId}`);
          if (saved) return new Set(JSON.parse(saved));
        }
      }
    } catch {}
    return new Set<string>();
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // 統計データを取得（自動更新を無効化）
  const onNavError = useCallback((error: string) => {
    devError('ナビゲーション統計エラー:', error);
  }, []);
  const { stats, loading, error, refresh, incrementUnread, clearUnread } = useNavigationStats({
    autoRefresh: false, // 自動更新を無効化
    onError: onNavError,
  });
  
  // refresh関数を親コンポーネントに提供
  useEffect(() => {
    if (onRefreshProvided) {
      onRefreshProvided(refresh);
    }
  }, [onRefreshProvided, refresh]);

  // オペレーター統計データを取得（自動更新を無効化）
  const { 
    stats: operatorStats, 
    loading: operatorLoading, 
    error: operatorError,
    refresh: refreshOperators 
  } = useOperatorStats({
    autoRefresh: false, // 自動更新を無効化
    onError: (error) => devError('オペレーター統計エラー:', error),
  });

  // タグ統計データを取得（自動更新を無効化）
  const { 
    stats: tagStats, 
    loading: tagLoading, 
    error: tagError,
    refresh: refreshTags 
  } = useTagStats({
    autoRefresh: false,
    onError: (error) => devError('タグ統計エラー:', error),
  });

  // 固定タグ統計データを取得
  const {
    categories: fixedTagCategories,
    refresh: refreshFixedTags,
  } = useFixedTagStats({
    autoRefresh: false,
    onError: (error) => devError('固定タグ統計エラー:', error),
  });

  // 実際のDBデータから受信トレイアイテムを生成
  const waitingCount = stats.waiting || 0;
  
  const allCount = (stats.pending || 0)
    + (stats.active || 0)
    + waitingCount
    + (stats.paused || 0)
    + (stats.temp_completed || 0)
    + (stats.completed || 0)
    + (stats.uncompleted || 0);
    
  // アクティブなステータス（上部表示）
  const activeInboxItems: InboxItem[] = [
    { id: 'all', name: '全て', count: allCount, status: 'active', priority: 'high' },
    { id: 'pending', name: '未対応', count: stats.pending, status: 'pending', priority: 'high' },
    { id: 'active', name: '対応中', count: stats.active, status: 'active', priority: 'high' },
    { id: 'waiting', name: '返信待ち', count: waitingCount, status: 'waiting', priority: 'high' },
    { id: 'paused', name: '保留', count: stats.paused, status: 'paused', priority: 'medium' },
    { id: 'temp_completed', name: '一時完了', count: stats.temp_completed || 0, status: 'temp_completed', priority: 'medium' },
  ];
  
  // 終了したステータス（下部表示）
  const closedInboxItems: InboxItem[] = [
    { id: 'completed', name: '完了', count: stats.completed, status: 'completed', priority: 'medium' },
    { id: 'uncompleted', name: '削除', count: stats.uncompleted, status: 'archived', priority: 'low' },
  ];

  devLog('📊 NavigationSidebar 統計データ:', {
    selectedCategory,
    stats,
    allCount,
    activeItems: activeInboxItems.map(item => ({ id: item.id, name: item.name, count: item.count })),
    closedItems: closedInboxItems.map(item => ({ id: item.id, name: item.name, count: item.count }))
  });

  // リアルタイムイベントでバッジ更新
  useEffect(() => {
    socketClient.connect_socket();
    let ticking = false;
    const scheduleRefresh = () => {
      if (ticking) return;
      ticking = true;
      // 次フレームでまとめて1回だけ実行（スロットル）
      requestAnimationFrame(() => {
        refresh();
        ticking = false;
      });
    };

    const onNew = (e: any) => {
      incrementUnread();
      scheduleRefresh();
    };
    const onClear = () => {
      clearUnread?.();
      scheduleRefresh();
    };
    const onRoomAllRead = () => {
      scheduleRefresh();
    };
    window.addEventListener('rt:newMessage', onNew as EventListener);
    window.addEventListener('rt:clearUnread', onClear as EventListener);
    window.addEventListener('rt:roomAllRead', onRoomAllRead as EventListener);
    return () => {
      window.removeEventListener('rt:newMessage', onNew as EventListener);
      window.removeEventListener('rt:clearUnread', onClear as EventListener);
      window.removeEventListener('rt:roomAllRead', onRoomAllRead as EventListener);
    };
  }, [incrementUnread, clearUnread, refresh]);

  // 現在ログイン中のユーザー情報を取得
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  useEffect(() => {
    try {
      const adminUserStr = localStorage.getItem('adminUser');
      if (adminUserStr) {
        const adminUser = JSON.parse(adminUserStr);
        setCurrentUserId(adminUser?.id || null);
      }
    } catch (error) {
      console.error('ログインユーザー情報取得エラー:', error);
    }
  }, []);

  // 実際のDBデータからオペレーターリストを取得し、並び替え
  const operators: OperatorItem[] = React.useMemo(() => {
    const operatorList = operatorStats.operators.map(op => ({
      id: op.id,
      name: op.name,
      email: op.email,
      role: op.role,
      status: op.status,
      activeChats: op.activeChats,
      totalChats: (op as any).totalChats ?? 0,
    }));

    // 並び替え: 現在のログインユーザーを最初に、その後は件数の多い順
    return operatorList.sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return b.totalChats - a.totalChats;
    });
  }, [operatorStats.operators, currentUserId]);

  // 実際のDBデータからタグリストを取得
  const tags: TagItem[] = [
    ...tagStats.dynamicTags.map(tag => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
      count: tag.count,
      category: 'カスタム' as const,
    })),
  ];

  // セクション展開切り替え（ユーザーごとにlocalStorageへ保存）
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      try {
        const adminUserStr = localStorage.getItem('adminUser');
        if (adminUserStr) {
          const userId = JSON.parse(adminUserStr)?.id;
          if (userId) {
            localStorage.setItem(`nav_accordion_${userId}`, JSON.stringify(Array.from(newSet)));
          }
        }
      } catch {}
      return newSet;
    });
  };



  // ステータス別の色を取得
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return '#4caf50';
      case 'away': return '#ff9800';
      case 'offline': return '#9e9e9e';
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        border: 'none',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          p: 2,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          size="medium"
          onClick={() => {
            if (window.confirm('本当に作成しますか？')) {
              setCreateModalOpen(true);
            }
          }}
          sx={{ width: '100%', py: 1 }}
        >
          チャットルーム新規作成
        </Button>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="warning" sx={{ m: 1, fontSize: '0.7rem' }}>
          データ取得エラー: {String(error)}
        </Alert>
      )}

      {/* メインコンテンツ */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* 受信トレイ一覧 */}
        <Accordion expanded={expandedSections.has('inbox')} onChange={() => toggleSection('inbox')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InboxIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                受信トレイ
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List dense>
              {/* アクティブなステータス */}
              {activeInboxItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => onCategorySelect(item.id)}
                    selected={selectedCategory === item.id}
                    sx={{ 
                      px: 2, 
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(item.priority),
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                    <Chip
                      label={item.count}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              
              {/* 区切り線 */}
              <Divider sx={{ my: 1 }} />
              
              {/* 終了したステータス */}
              {closedInboxItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => onCategorySelect(item.id)}
                    selected={selectedCategory === item.id}
                    sx={{ 
                      px: 2, 
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(item.priority),
                          opacity: 0.5,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{ 
                        fontSize: '0.875rem',
                        color: 'text.secondary'
                      }}
                    />
                    <Chip
                      label={item.count}
                      size="small"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem',
                        opacity: 0.7
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* アカウント一覧 */}
        <Accordion expanded={expandedSections.has('operators')} onChange={() => toggleSection('operators')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                アカウント一覧
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List dense>
              {operators.map((operator) => {
                const isCurrentUser = operator.id === currentUserId;
                return (
                  <ListItem key={operator.id} disablePadding>
                    <ListItemButton 
                      sx={{ 
                        px: 2, 
                        py: 0.5,
                        backgroundColor: isCurrentUser ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isCurrentUser ? 'rgba(25, 118, 210, 0.12)' : undefined,
                        },
                      }}
                      onClick={() => {
                        const key = operator.id || `name:${operator.name}`;
                        onCategorySelect?.(`operator:${key}`);
                      }}
                      selected={selectedCategory === `operator:${operator.id}` || selectedCategory === `operator:name:${operator.name}`}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                          {operator.name.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={operator.name}
                        secondary={operator.email}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={operator.totalChats}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* 固定タグ（商材・対応部署等） */}
        {fixedTagCategories.map((cat) => (
          <Accordion
            key={cat.category_id}
            expanded={expandedSections.has(`fixed-${cat.category_id}`)}
            onChange={() => toggleSection(`fixed-${cat.category_id}`)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TagIcon color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                  {cat.category_name}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List dense>
                {cat.options.map((opt) => (
                  <ListItem key={opt.option_id} disablePadding>
                    <ListItemButton
                      onClick={() => onCategorySelect?.(`fixedtag:${opt.option_id}`)}
                      selected={selectedCategory === `fixedtag:${opt.option_id}`}
                      sx={{ px: 2, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#ff9800',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={opt.option_name}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                      <Chip
                        label={opt.count}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* お問合せタグ一覧 */}
        <Accordion expanded={expandedSections.has('tags')} onChange={() => toggleSection('tags')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LabelIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                お問合せタグ
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List dense>
              {tags.map((tag) => (
                <ListItem key={tag.id} disablePadding>
                  <ListItemButton
                    onClick={() => onCategorySelect?.(`tag:${tag.id}`)}
                    selected={selectedCategory === `tag:${tag.id}` || selectedCategory === `tag-${tag.id}`}
                    sx={{ px: 2, py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: tag.color,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={tag.name}
                      secondary={tag.category}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={tag.count}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 1 }} />

        {/* 管理機能 */}
        <List dense>
          {/* 分析・レポート（管理者・運営者） */}
          <ListItem disablePadding>
            <ListItemButton component="a" href="/admin/analytics">
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AnalyticsIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="分析・レポート"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>

          {/* ルーム管理 */}
          <ListItem disablePadding>
            <ListItemButton component="a" href="/admin/rooms">
              <ListItemIcon sx={{ minWidth: 32 }}>
                <RoomManageIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="ルーム管理"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>

          {/* アカウント管理・システム設定（管理者のみ） */}
          {isAdmin() && (
            <>
              <ListItem disablePadding>
                <ListItemButton component="a" href="/admin/accounts">
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <PeopleIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="アカウント管理"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component="a" href="/admin/settings">
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <SettingsIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="システム設定"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}

          <Divider sx={{ my: 0.5 }} />

          {/* ヘルプ・マニュアル */}
          <ListItem disablePadding>
            <ListItemButton component="a" href="/admin/help">
              <ListItemIcon sx={{ minWidth: 32 }}>
                <MenuBookIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="ヘルプ・マニュアル"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>

          {/* 更新情報 */}
          <ListItem disablePadding>
            <ListItemButton component="a" href="/admin/release-notes">
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ReleaseIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="更新情報"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* 新規チャットルーム作成モーダル */}
      <CreateChatRoomModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(chatRoom) => {
          devLog('チャットルーム作成完了:', chatRoom);
          setCreateModalOpen(false);
          refresh(); // 統計を更新
        }}
      />


    </Box>
  );
};